'use client'

// NavMobileToggle — the interactive <nav> wrapper of one header copy (#118
// server-chrome, Step 3). It owns the LOCAL `showNavMobile` state that the old
// class <Header> owned (the mobile hamburger menu open/close), plus the two
// behaviours the class attached to it:
//   - body-scroll lock while the menu is open (document.body.style.overflow),
//   - auto-hide the menu on navigation, driven by usePathnameCompat() (the
//     router-agnostic pathname the old HeaderWithPathname wrapper fed into
//     componentDidUpdate → hideMenu).
//
// It renders the <nav> element: the <ul> whose open-class toggles on
// showNavMobile, and the mobile hamburger <button>. The nav ITEMS (the category
// <li> list + the CTA <li>) are the STATIC, server-eligible part — they arrive as
// `children` (server JSX from HeaderNavServer), so this island stays narrow: it
// owns only the toggle, never the item list.
//
// Two copies of the header render (live + fixed scroll-clone), so two of these
// islands mount — each owns its own showNavMobile. That is behaviourally
// equivalent to the old single shared state: the clone is an off-screen layout
// spacer (the live/fixed copy is the only one the user ever clicks), and both
// copies start closed, so the SSR HTML is identical. Body-scroll lock is global
// either way.

import React, { useState, useEffect, useRef } from 'react'
import headerStyles from './styles.module.css'
import styles from './HeaderNav/styles.module.css'
import usePathnameCompat from '../../utils/usePathnameCompat'

function NavMobileToggle({ mobileOpenIcon, mobileCloseIcon, children }) {
  const [showNavMobile, setShowNavMobile] = useState(false)
  const pathname = usePathnameCompat()

  const disableScroll = () => { document.body.style.overflow = 'hidden' }
  const enableScroll = () => { document.body.style.overflow = '' }

  // Auto-hide the menu whenever the pathname changes (i.e. after a navigation) —
  // the router-agnostic replacement for the legacy Pages-Router
  // `routeChangeComplete` listener, ported from the class's componentDidUpdate.
  // Skip the initial mount (the menu already starts closed) so we don't touch
  // body overflow on first render.
  const isFirst = useRef(true)
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false
      return
    }
    setShowNavMobile(false)
    enableScroll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // Release the scroll lock if this island unmounts while the menu is open.
  useEffect(() => {
    return () => enableScroll()
  }, [])

  const handleMenuToggle = () => {
    setShowNavMobile((prev) => {
      const next = !prev
      // don't allow scrolling when mobile nav is open
      next ? disableScroll() : enableScroll()
      return next
    })
  }

  return (
    <nav className={styles.navigation} aria-label="Primary">
      <ul className={showNavMobile ? `${styles.navItems} ${styles.navItemsOpen}` : styles.navItems}>
        {children}
      </ul>

      <button className={`${headerStyles.showNavButton} ${styles.mobileNavButton}`} aria-label="Toggle navigation menu" onClick={handleMenuToggle}>
        {/* eslint-disable-next-line local-rules/prefer-img-component, local-rules/img-requires-dimensions */}
        <img className={!showNavMobile ? `${styles.mobileOpenIcon} ${styles.iconShown}` : styles.mobileOpenIcon} src={mobileOpenIcon?.src} alt="Open menu" loading="eager" decoding="async" data-lazy-exempt width={32} height={32}/>
        {/* eslint-disable-next-line local-rules/prefer-img-component, local-rules/img-requires-dimensions */}
        <img className={showNavMobile ? `${styles.mobileCloseIcon} ${styles.iconShown}` : styles.mobileCloseIcon} src={mobileCloseIcon?.src} alt="Close menu" loading="eager" decoding="async" data-lazy-exempt width={32} height={32}/>
      </button>
    </nav>
  )
}

export default NavMobileToggle
